import { MessageCard } from '@manulife/mux';
import { useMemo } from 'react';
import styled from 'styled-components';
import useAEMContent, { FRAGMENT_GB_SHARED, FRAGMENT_MP } from '../../CustomHooks/useAEMContent';
import useShallowEqualSelector from '../../CustomHooks/useShallowEqualSelector';
import { canAddFamily } from './utils';

const AlertWrapper = styled.div`
    margin-bottom: 25px;
`;

const DynamicMessaging = () => {
    const {
        features,
        memberBenefits,
        familyMembers,
        allowedDependentActions,
        studentMaxAge,
        childDependentMaxAge: studentMinAge,
    } = useShallowEqualSelector((state) => ({
        features: state.Features.data,
        memberBenefits: state.MemberData.memberBenefits,
        familyMembers: state.MemberData.familyMembers,
        allowedDependentActions: state.MemberData.allowedDependentActions,
        studentMaxAge: state.MemberData.member.plan.studentMaxAge,
        childDependentMaxAge: state.MemberData.member.plan.childDependentMaxAge,
    }));

    const manageLifeEventOnMmb = features['lifeEvent'] || features['lifeEventHold'];
    const {
        addSpouse: allowedToAddSpouse,
        addChild: allowedToAddChild,
        updateSpouse: allowedToUpdateSpouse,
        updateChild: allowedToUpdateChild,
        extendStudent: allowedToExtendStudent,
    } = allowedDependentActions;
    const isStudentStatusExtendable = familyMembers.some(
        (familyMember) => familyMember.isStudentStatusExtendable && familyMember.specialDependent !== 'DISABLED'
    );

    // check to see what options a member is allowed to do
    const showAddFamilyNote = useMemo(
        () =>
            !manageLifeEventOnMmb &&
            !canAddFamily(
                allowedDependentActions,
                features,
                familyMembers,
                memberBenefits,
                studentMaxAge,
                studentMinAge
            ),
        [
            manageLifeEventOnMmb,
            allowedDependentActions,
            features,
            familyMembers,
            memberBenefits,
            studentMaxAge,
            studentMinAge,
        ]
    );
    const showRemoveFamilyNote = !manageLifeEventOnMmb;
    const showChangeCoverageNote = !manageLifeEventOnMmb;
    const showChangeDependentCobNote = !allowedToUpdateSpouse || !allowedToUpdateChild || !features['COB'];
    const showAddChildNote = !allowedToAddChild && allowedToAddSpouse;
    const showAddSpouseNote = !allowedToAddSpouse && allowedToAddChild;
    const showChangeDependentStudentStatusNote =
        (!features['studentStatus'] || !allowedToExtendStudent) && isStudentStatusExtendable;

    // If member can do all options that are listed under contact your plan administrator, hide the section
    const showPAMessage = useMemo(
        () =>
            showAddFamilyNote ||
            showRemoveFamilyNote ||
            showChangeCoverageNote ||
            showAddChildNote ||
            showAddSpouseNote ||
            showChangeDependentCobNote ||
            showChangeDependentStudentStatusNote,
        [
            showAddFamilyNote,
            showRemoveFamilyNote,
            showChangeCoverageNote,
            showAddChildNote,
            showAddSpouseNote,
            showChangeDependentCobNote,
            showChangeDependentStudentStatusNote,
        ]
    );

    const contactCustomerServiceAlertText = useAEMContent(FRAGMENT_MP.landing, 'contactCustomerServiceAlertText');
    const contactPAMessage = useAEMContent(FRAGMENT_MP.landing, 'contactPAMessageFamily', { parseHTML: true });
    const addFamilyMember = useAEMContent(FRAGMENT_MP.landing, 'addFamilyMember');
    const removeFamilyMember = useAEMContent(FRAGMENT_MP.landing, 'removeFamilyMember');
    const changeCoverage = useAEMContent(FRAGMENT_MP.landing, 'changeCoverage');
    const changeDependentCob = useAEMContent(FRAGMENT_MP.landing, 'changeDependentCob');
    const addSpouse = useAEMContent(FRAGMENT_MP.landing, 'addSpouse');
    const addChild = useAEMContent(FRAGMENT_MP.landing, 'addChild');
    const changeDependentStudentStatus = useAEMContent(FRAGMENT_MP.landing, 'changeDependentStudentStatus');

    const callCenterPhoneNumber = useAEMContent(FRAGMENT_GB_SHARED.cscPhoneNumber, 'callCenterPhoneNumber');

    return (
        <>
            {features['lifeEventHold'] && (
                <AlertWrapper>
                    <MessageCard
                        variant={MessageCard.VARIANT.SECONDARY_INFORMATION}
                        id="contactCustomerServiceAlert"
                        data-testid="contactCustomerServiceAlert"
                    >
                        {`${contactCustomerServiceAlertText} ${callCenterPhoneNumber}`}
                    </MessageCard>
                </AlertWrapper>
            )}
            {showPAMessage && (
                <>
                    <div data-testid="contactPAMessage">{contactPAMessage}</div>
                    <ul>
                        {showAddFamilyNote && <li data-testid="addFamilyMemberPANote">{addFamilyMember}</li>}
                        {showRemoveFamilyNote && <li data-testid="removeFamilyMemberPANote">{removeFamilyMember}</li>}
                        {showChangeCoverageNote && <li data-testid="changeCoveragePANote">{changeCoverage}</li>}
                        {showChangeDependentCobNote && (
                            <li data-testid="changeDependentCobPANote">{changeDependentCob}</li>
                        )}
                        {showAddSpouseNote && <li data-testid="addSpousePANote">{addSpouse}</li>}
                        {showAddChildNote && <li data-testid="addChildPANote">{addChild}</li>}
                        {showChangeDependentStudentStatusNote && (
                            <li data-testid="changeDependentStudentStatusPANote">{changeDependentStudentStatus}</li>
                        )}
                    </ul>
                </>
            )}
        </>
    );
};

export default DynamicMessaging;
